// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; field captions are visual labels for the adjacent control (same convention as SchoolsAdmin / SupplierAdmin).
//
// =============================================================================
// UnleashedProductsAdmin — the Products catalogue screen (Manage → Unleashed →
// Products)
// =============================================================================
// Admin/SuperUser editor for `unleashed_products` (db-64): the editable copy of
// the Unleashed product catalogue. Searchable list with a group filter,
// deactivate/reactivate, "+ New product" and Edit via an Unleashed-style modal.
// Reads/writes go through the src/data/unleashedProducts repo. This is the
// pick-list the Order Builder's product mapping (db-65) will point at.
// Reference: docs/unleashed-interface.md.
// =============================================================================
import React from "react";
import { createProduct, listProducts, updateProductById } from "../../data/unleashedProducts";
import { usePageSize } from "../../hooks/usePageSize";
import { confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";
import { ProductSyncModal } from "./ProductSyncModal.jsx";

const LIST_GRID = { gridTemplateColumns: "220px 1fr 170px 60px 200px" };

// Rows per page in the catalogue list (matches the Dropdown Lists screen).
// Rows per page is the shared per-device preference (services/pageSize.ts).

const EMPTY_FORM = {
  product_code: "",
  product_description: "",
  product_group: "",
  base_pack: "",
  base_unit: "",
};

// ── Create / edit modal (Unleashed "Add" style: columned, sized to the data) ──
function ProductFormModal({ mode, row, groups, onClose, onSaved }) {
  const [form, setForm] = React.useState(function () {
    if (mode === "edit" && row) {
      return {
        product_code: row.product_code ?? "",
        product_description: row.product_description ?? "",
        product_group: row.product_group ?? "",
        base_pack: row.base_pack ?? "",
        base_unit: row.base_unit ?? "",
      };
    }
    return { ...EMPTY_FORM };
  });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");

  function set(key, val) {
    setForm(function (f) {
      return { ...f, [key]: val };
    });
  }

  async function save() {
    const code = form.product_code.trim();
    if (!code) {
      setErr("Product Code is required.");
      return;
    }
    setSaving(true);
    setErr("");
    // Empty text fields are stored as NULL, not "".
    const body = {
      product_code: code,
      product_description: form.product_description.trim() || null,
      product_group: form.product_group.trim() || null,
      base_pack: form.base_pack.trim() || null,
      base_unit: form.base_unit.trim() || null,
    };
    const { error } =
      mode === "edit" ? await updateProductById(row.id, body) : await createProduct(body);
    setSaving(false);
    if (error) {
      // The unique(product_code) violation is the common one.
      setErr(
        /duplicate|unique/i.test(error.message)
          ? `Product code "${code}" already exists.`
          : `Save failed: ${error.message}`
      );
      return;
    }
    logAudit(mode === "edit" ? "unleashed_product_updated" : "unleashed_product_created", {
      id: row?.id,
      product_code: code,
    });
    onSaved(mode);
  }

  const labelCls = "text-[12px] text-zephyr-muted text-right pr-3 pt-2";
  const inputCls =
    "py-[6px] px-[9px] rounded-[6px] border border-zephyr-border text-[13px] text-zephyr-text bg-white";

  return (
    <ModalBackdrop
      onClose={onClose}
      portal
      contentStyle={{ maxWidth: 620, background: "#fff", borderRadius: 12, padding: "16px 20px" }}
    >
      <div className="p-1">
        <div className="flex justify-between items-center mb-4">
          <h2 className="m-0 text-[20px] text-zephyr-teal-dark">
            {mode === "edit" ? "Edit product" : "New product"}
          </h2>
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="bg-transparent border border-zephyr-border rounded-[6px] py-[6px] px-[14px] text-[13px] cursor-pointer text-zephyr-text"
              style={{ fontFamily: "inherit" }}
            >
              Cancel
            </button>
            <Btn onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Btn>
          </div>
        </div>

        {err && (
          <div className="mb-3 text-[13px] text-[#C62828] bg-[#FFF3F3] border border-[#E0A0A0] rounded-[6px] py-2 px-3">
            {err}
          </div>
        )}

        <div className="grid items-start gap-y-2" style={{ gridTemplateColumns: "130px 1fr" }}>
          <label className={labelCls}>Product Code</label>
          <input
            aria-label="Product Code"
            value={form.product_code}
            onChange={function (e) {
              set("product_code", e.target.value);
            }}
            className={`${inputCls} w-[240px]`}
            style={{ fontFamily: "inherit" }}
          />

          <label className={labelCls}>Description</label>
          <input
            aria-label="Description"
            value={form.product_description}
            onChange={function (e) {
              set("product_description", e.target.value);
            }}
            className={`${inputCls} w-full`}
            style={{ fontFamily: "inherit" }}
          />

          <label className={labelCls}>Group</label>
          <input
            aria-label="Group"
            value={form.product_group}
            onChange={function (e) {
              set("product_group", e.target.value);
            }}
            list="unleashed-product-groups"
            className={`${inputCls} w-[240px]`}
            style={{ fontFamily: "inherit" }}
          />

          <label className={labelCls}>Base Pack</label>
          <input
            aria-label="Base Pack"
            value={form.base_pack}
            onChange={function (e) {
              set("base_pack", e.target.value);
            }}
            className={`${inputCls} w-[120px]`}
            style={{ fontFamily: "inherit" }}
          />

          <label className={labelCls}>Base Unit</label>
          <input
            aria-label="Base Unit"
            value={form.base_unit}
            onChange={function (e) {
              set("base_unit", e.target.value);
            }}
            className={`${inputCls} w-[100px]`}
            style={{ fontFamily: "inherit" }}
          />
        </div>

        <datalist id="unleashed-product-groups">
          {groups.map(function (g) {
            return <option key={g} value={g} />;
          })}
        </datalist>
      </div>
    </ModalBackdrop>
  );
}

export function UnleashedProductsAdmin() {
  const [PAGE_SIZE] = usePageSize();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [groupFilter, setGroupFilter] = React.useState("All");
  const [savedMsg, setSavedMsg] = React.useState("");
  const [refreshTick, setRefreshTick] = React.useState(0);
  // null | { mode: "create" | "edit", row }
  const [modal, setModal] = React.useState(null);
  // Hide deactivated products by default; a toggle reveals them.
  const [showDeactivated, setShowDeactivated] = React.useState(false);
  const [page, setPage] = React.useState(0);
  // null when closed; open the draggable "Sync from Unleashed" modal when true.
  const [syncOpen, setSyncOpen] = React.useState(false);

  // The catalogue is small (~245 rows, well under the 1,000-row PostgREST cap),
  // so load it all once and filter client-side for a snappy list + an accurate
  // group dropdown.
  React.useEffect(
    function () {
      let cancelled = false;
      setLoading(true);
      listProducts().then(function (data) {
        if (cancelled) return;
        if (data) setRows(data);
        setLoading(false);
      });
      return function () {
        cancelled = true;
      };
    },
    [refreshTick]
  );

  function refresh() {
    setRefreshTick(function (n) {
      return n + 1;
    });
  }

  // Back to the first page whenever the filters change (not on an inline save,
  // which reloads the same list).
  React.useEffect(
    function () {
      setPage(0);
    },
    [search, groupFilter, showDeactivated]
  );

  function showSaved(msg) {
    setSavedMsg(msg || "Saved ✓");
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  function onModalSaved(mode) {
    setModal(null);
    refresh();
    showSaved(mode === "create" ? "Product created ✓" : "Saved ✓");
  }

  async function toggleDeactivate(r) {
    if (!r.deactivated) {
      const ok = await confirmDialog({
        title: "Deactivate product",
        message: `Deactivate "${r.product_code}"? It will be hidden from the product mapping pick-list. You can reactivate it here at any time.`,
        confirmLabel: "Deactivate",
        danger: true,
      });
      if (!ok) return;
    }
    const next = !r.deactivated;
    const { error } = await updateProductById(r.id, { deactivated: next });
    if (error) {
      showSaved("Update failed");
      return;
    }
    logAudit(next ? "unleashed_product_deactivated" : "unleashed_product_reactivated", {
      id: r.id,
      product_code: r.product_code,
    });
    refresh();
    showSaved(next ? "Product deactivated ✓" : "Product reactivated ✓");
  }

  // Distinct groups for the filter dropdown + the modal datalist.
  const groups = React.useMemo(
    function () {
      const set = new Set();
      for (const r of rows) if (r.product_group) set.add(r.product_group);
      return [...set].sort(function (a, b) {
        return a.localeCompare(b);
      });
    },
    [rows]
  );

  const q = search.trim().toLowerCase();
  const shown = rows.filter(function (r) {
    if (!showDeactivated && r.deactivated) return false;
    if (groupFilter !== "All" && (r.product_group || "") !== groupFilter) return false;
    if (!q) return true;
    return (
      (r.product_code || "").toLowerCase().includes(q) ||
      (r.product_description || "").toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = shown.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      <div className="mb-1 text-[12px] text-zephyr-muted font-semibold">Unleashed</div>
      <div className="flex justify-between items-center mb-3">
        <h1 className="m-0 text-[22px] text-zephyr-text">Products</h1>
        {savedMsg && <span className="text-[13px] text-[#2E7D32] font-semibold">{savedMsg}</span>}
      </div>
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        The Unleashed product catalogue. Add, edit or deactivate a product. This is the pick-list
        the Order Builder's product mapping will draw from.
      </p>

      <div className="flex gap-4 items-center mb-4 flex-wrap">
        <input
          value={search}
          onChange={function (e) {
            setSearch(e.target.value);
          }}
          placeholder="Search code or description…"
          className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white w-[300px]"
          style={{ fontFamily: "inherit" }}
        />
        <select
          aria-label="Filter by group"
          value={groupFilter}
          onChange={function (e) {
            setGroupFilter(e.target.value);
          }}
          className="py-2 px-3 rounded-[8px] border border-zephyr-border text-[13px] text-zephyr-text bg-white"
          style={{ fontFamily: "inherit" }}
        >
          <option value="All">All groups</option>
          {groups.map(function (g) {
            return (
              <option key={g} value={g}>
                {g}
              </option>
            );
          })}
        </select>
        <span className="text-[12px] text-zephyr-muted">
          {shown.length} product{shown.length === 1 ? "" : "s"}
        </span>
        <label className="flex items-center gap-2 text-[12px] text-zephyr-muted cursor-pointer">
          <input
            type="checkbox"
            checked={showDeactivated}
            onChange={function (e) {
              setShowDeactivated(e.target.checked);
            }}
          />
          Show deactivated
        </label>
        <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          <Btn
            onClick={function () {
              setSyncOpen(true);
            }}
          >
            Sync from Unleashed
          </Btn>
          <Btn
            onClick={function () {
              setModal({ mode: "create", row: null });
            }}
          >
            + New product
          </Btn>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-zephyr-muted p-10">Loading…</div>
      ) : (
        <div className="border border-zephyr-border rounded-[10px] overflow-hidden">
          <div
            className="grid gap-0 bg-zephyr-teal-dark text-white py-[7px] px-[13px] text-[11px] font-bold uppercase tracking-[0.5px]"
            style={LIST_GRID}
          >
            <div>Product Code</div>
            <div>Description</div>
            <div>Group</div>
            <div className="text-center">Unit</div>
            <div>Actions</div>
          </div>
          {pageRows.map(function (r, idx) {
            return (
              <div
                key={r.id}
                style={{
                  borderBottom: idx < pageRows.length - 1 ? "1px solid #CFE0DE" : "none",
                  background: r.deactivated ? "#FFF3F3" : idx % 2 === 0 ? "#FFFFFF" : "#F7F9F9",
                }}
              >
                <div className="grid gap-0 py-[7px] px-[13px] items-center" style={LIST_GRID}>
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent border-0 p-0 font-semibold text-[13px] underline cursor-pointer text-left"
                      style={{
                        color: r.deactivated ? "#607D79" : "#00695C",
                        fontFamily: "inherit",
                      }}
                    >
                      {r.product_code}
                    </button>
                    {r.deactivated && (
                      <span className="ml-[6px] text-[10px] font-bold text-white bg-[#C62828] rounded-[3px] py-[1px] px-[6px]">
                        DEACTIVATED
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-zephyr-text overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.product_description || "—"}
                  </div>
                  <div className="text-[12px] text-zephyr-muted overflow-hidden text-ellipsis whitespace-nowrap">
                    {r.product_group || "—"}
                  </div>
                  <div className="text-center text-[12px] text-zephyr-muted">
                    {r.base_unit || "—"}
                  </div>
                  <div className="flex gap-[6px]">
                    <button
                      type="button"
                      onClick={function () {
                        setModal({ mode: "edit", row: r });
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border border-[#CFE0DE] text-[#1A2E2B]"
                      style={{ fontFamily: "inherit" }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={function () {
                        toggleDeactivate(r);
                      }}
                      className="bg-transparent rounded-[6px] py-1 px-[10px] text-[11px] cursor-pointer border"
                      style={{
                        fontFamily: "inherit",
                        borderColor: r.deactivated ? "#2E7D32" : "#E0A0A0",
                        color: r.deactivated ? "#2E7D32" : "#C62828",
                      }}
                    >
                      {r.deactivated ? "Reactivate" : "Deactivate"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {shown.length === 0 && (
            <div className="p-10 text-center text-zephyr-muted text-[13px]">No products found.</div>
          )}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex gap-[6px] items-center justify-center mt-4 flex-wrap">
          <button
            type="button"
            onClick={function () {
              setPage(Math.max(0, safePage - 1));
            }}
            disabled={safePage === 0}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, function (_, i) {
            return i;
          }).map(function (i) {
            const active = i === safePage;
            return (
              <button
                key={i}
                type="button"
                onClick={function () {
                  setPage(i);
                }}
                className={`border rounded-[6px] px-[11px] py-[4px] cursor-pointer text-[13px] ${
                  active
                    ? "bg-zephyr-teal-dark border-zephyr-teal-dark text-white font-bold"
                    : "bg-white border-zephyr-border text-zephyr-text"
                }`}
              >
                {i + 1}
              </button>
            );
          })}
          <button
            type="button"
            onClick={function () {
              setPage(Math.min(totalPages - 1, safePage + 1));
            }}
            disabled={safePage >= totalPages - 1}
            className="border border-zephyr-border rounded-[6px] px-[10px] py-[4px] cursor-pointer bg-white text-zephyr-text disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {modal && (
        <ProductFormModal
          mode={modal.mode}
          row={modal.row}
          groups={groups}
          onClose={function () {
            setModal(null);
          }}
          onSaved={onModalSaved}
        />
      )}

      {syncOpen && (
        <ProductSyncModal
          onClose={function () {
            setSyncOpen(false);
          }}
          onApplied={refresh}
        />
      )}
    </div>
  );
}
